import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Select, Tag, Spin, Empty, InputNumber } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { listCitas } from "../../api/agendaApi";
import {
  TIPO_CITA_LABELS, ESTADO_OPTIONS, ESTADO_COLORS, ESTADO_LABELS, formatHora,
} from "../../components/agenda/agendaHelpers";
import useDoctorOptions from "../../components/agenda/useDoctorOptions";
import CitaFormModal from "../../components/agenda/CitaFormModal";
import CitaDetalleModal from "../../components/agenda/CitaDetalleModal";
import { toast } from "../../components/toasts/toastStore";
import "./PacientesPage.css";
import "../../components/agenda/agenda.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

const { RangePicker } = DatePicker;

// Vista de Agenda (Citas) para Visum Oftalmologia. No hay libreria de
// calendario visual instalada en este proyecto (ver package.json: solo
// antd/dayjs/date-fns, nada tipo react-big-calendar) y el patron real de
// Agenda de multiproductos (ver skill nuevo-modulo-estilo-agenda) es un
// dominio distinto (eventos genericos con recurrencia/documento). Aqui el
// dominio es Citas clinicas con su propio modelo (participantes, cirugia,
// autorizacion de menores, consentimientos, medios), asi que se optó por un
// listado agrupado por dia (mas util para una agenda clinica con filtros de
// doctor/paciente/estado que por un grid visual de horas).
export default function AgendaPage() {
  const { perm } = usePermisos() || { perm: () => true };
  const [citas, setCitas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [rango, setRango] = useState([dayjs().startOf("day"), dayjs().add(6, "day").endOf("day")]);
  const [estado, setEstado] = useState(undefined);
  const [idDoctor, setIdDoctor] = useState(undefined);

  const [formOpen, setFormOpen] = useState(false);
  const [idCitaDetalle, setIdCitaDetalle] = useState(null);

  const { doctores, disponible: doctoresDisponibles } = useDoctorOptions();

  const cargar = () => {
    setCargando(true);
    listCitas({
      fecha_inicio: rango?.[0] ? rango[0].format("YYYY-MM-DD 00:00:00") : undefined,
      fecha_fin: rango?.[1] ? rango[1].format("YYYY-MM-DD 23:59:59") : undefined,
      estado,
      id_user_doctor: idDoctor,
    })
      .then((data) => setCitas(data?.items || []))
      .catch(() => toast.error("No se pudieron cargar las citas"))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango, estado, idDoctor]);

  const grupos = useMemo(() => {
    const porDia = new Map();
    citas
      .slice()
      .sort((a, b) => dayjs(a.fecha_hora_inicio).valueOf() - dayjs(b.fecha_hora_inicio).valueOf())
      .forEach((c) => {
        const dia = dayjs(c.fecha_hora_inicio).format("YYYY-MM-DD");
        if (!porDia.has(dia)) porDia.set(dia, []);
        porDia.get(dia).push(c);
      });
    return Array.from(porDia.entries());
  }, [citas]);

  return (
    <main className="agc-main">
      <div className="agc-header">
        <div>
          <h1 className="agc-title">Agenda</h1>
          <p className="agc-subtitle">Citas de pacientes: consultas, seguimientos, cirugías y optometría.</p>
        </div>
        {perm("agenda", "insertar") && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setFormOpen(true)}
            style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
          >
            Nueva cita
          </Button>
        )}
      </div>

      <div className="agc-filters">
        <RangePicker
          value={rango}
          onChange={(val) => setRango(val || [dayjs().startOf("day"), dayjs().add(6, "day").endOf("day")])}
          format="DD/MM/YYYY"
          allowClear={false}
        />
        <Select
          allowClear
          placeholder="Estado"
          style={{ width: 180 }}
          value={estado}
          onChange={setEstado}
          options={ESTADO_OPTIONS}
        />
        {doctoresDisponibles ? (
          <Select
            allowClear
            showSearch
            placeholder="Doctor"
            style={{ width: 220 }}
            optionFilterProp="label"
            value={idDoctor}
            onChange={setIdDoctor}
            options={doctores.map((d) => ({ value: d.id_user, label: d.nombre }))}
          />
        ) : (
          <InputNumber placeholder="ID doctor" min={1} value={idDoctor} onChange={setIdDoctor} style={{ width: 140 }} />
        )}
        <Button className="paq-btn-clean agc-btn-clean" icon={<ReloadOutlined />} onClick={cargar}>Actualizar</Button>
      </div>

      {cargando && citas.length === 0 ? (
        <div className="agc-loading"><Spin /></div>
      ) : grupos.length === 0 ? (
        <Empty description="No hay citas en este rango" style={{ marginTop: 60 }} />
      ) : (
        <div className="agc-grupos">
          {grupos.map(([dia, items]) => (
            <div className="agc-grupo" key={dia}>
              <div className="agc-grupo-fecha">{dayjs(dia).format("dddd D [de] MMMM")}</div>
              <div className="agc-grupo-items">
                {items.map((c) => (
                  <button
                    key={c.id_cita}
                    className="agc-cita-card"
                    onClick={() => setIdCitaDetalle(c.id_cita)}
                  >
                    <span className="agc-cita-hora">{formatHora(c.fecha_hora_inicio)}</span>
                    <span className="agc-cita-paciente">{c.paciente_nombre || "Paciente"}</span>
                    <span className="agc-cita-tipo">{TIPO_CITA_LABELS[c.tipo_cita] || c.tipo_cita}</span>
                    {c.doctor_nombre && <span className="agc-cita-doctor">Dr(a). {c.doctor_nombre}</span>}
                    {c.espacio_nombre && <span className="agc-cita-espacio">{c.espacio_nombre}</span>}
                    <Tag color={ESTADO_COLORS[c.estado] || "default"} className="agc-cita-estado">
                      {ESTADO_LABELS[c.estado] || c.estado}
                    </Tag>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <CitaFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={() => {
          setFormOpen(false);
          cargar();
        }}
      />

      <CitaDetalleModal
        open={Boolean(idCitaDetalle)}
        idCita={idCitaDetalle}
        onClose={() => setIdCitaDetalle(null)}
        onChanged={cargar}
        onNavigateCita={(id) => setIdCitaDetalle(id)}
      />
    </main>
  );
}
